import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { ScanLine, Plus } from "lucide-react";
import { toast } from "sonner";
import FileUpload from "@/components/FileUpload";

export default function RadiologyPage() {
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", modality: "X-Ray", body_part: "", notes: "" });

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([api.get("/radiology"), api.get("/patients")]);
    setItems(r.data); setPatients(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/radiology", form);
    toast.success("Imaging request created");
    setOpen(false); setForm({ patient_id: "", modality: "X-Ray", body_part: "", notes: "" });
    load();
  };

  return (
    <div className="space-y-5 fade-up" data-testid="radiology-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="text-3xl font-bold tracking-tight">Radiology</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} imaging requests</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="new-rad-btn"><Plus className="size-4" /> New request</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New imaging request</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1.5"><Label>Patient *</Label>
                <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Modality *</Label>
                <Select value={form.modality} onValueChange={(v) => setForm({ ...form, modality: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["X-Ray", "MRI", "CT Scan", "Ultrasound", "PET", "DICOM"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Body part *</Label>
                <Input value={form.body_part} onChange={(e) => setForm({ ...form, body_part: e.target.value })} required /></div>
              <div className="space-y-1.5"><Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              {form.patient_id ? (
                <div className="border-t border-border pt-3">
                  <FileUpload patientId={form.patient_id} resourceType="radiology" label="Attach images / DICOM" compact />
                </div>
              ) : (
                <div className="border-t border-border pt-3 text-[11px] text-muted-foreground">
                  Select a patient to attach imaging files (DICOM, JPG, PDF).
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-rad-btn">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => {
          const p = patients.find((x) => x.id === r.patient_id);
          return (
            <Card key={r.id}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold">{r.modality}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{r.body_part}</div>
                  </div>
                  <ScanLine className="size-5 text-primary opacity-60" />
                </div>
                <div className="mt-3 text-sm">{p?.name || "—"}</div>
                <div className="mt-2 mono text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
                <Badge variant="outline" className="mt-3">{r.status}</Badge>
              </CardContent>
            </Card>
          );
        })}
        {items.length === 0 && (
          <Card className="md:col-span-2 lg:col-span-3"><CardContent className="p-10 text-center text-muted-foreground">No requests yet.</CardContent></Card>
        )}
      </div>
    </div>
  );
}
